import { Formik, Form } from 'formik';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import styled from 'styled-components';
import { AutoSuggest } from 'components/Form/AutoSuggest';
import { Input } from 'components/Form/Input';
import Loading from 'components/Loading';
import { Modal } from 'components/Modal';
import { useGetGATickets } from 'pages/app/Cart/Components/gql/queries/useGetGATickets';
import { errorMessage, successMessage } from 'shared/alerts';
import { useGenerateVetTix } from '../gql/mutations/useGenerateVetTix';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;

  @media (max-width: 860px) {
    padding: 40px 20px;
  }
`;

const Title = styled.h2`
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  padding: 20px;
`;

const FieldWrapper = styled.div`
  margin-bottom: 20px;
`;

const SubmitButton = styled.button`
  background-color: #fa4616;
  color: white;
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  width: 100px;
  align-self: center;
  font-weight: 500;
  cursor: pointer;
`;

export const VetTixModal = ({ isVisible, setIsVisible }) => {
  const [tickets, setTickets] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { id } = useParams();

  const { data, loading } = useGetGATickets();

  const generateVetTix = useGenerateVetTix();

  useEffect(() => {
    if (isVisible) {
      if (data && data.getGATicketsV2 && data.getGATicketsV2.length > 0) {
        const tickets = data.getGATicketsV2
          ?.map((ticket) => ticket.tickets)
          .flat();

        const ticketOptions = tickets?.map((ticket) => ({
          label: ticket.name,
          value: ticket.id,
        }));
        setTickets(ticketOptions);
      }
    }
  }, [data, loading, isVisible]);

  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      const response = await generateVetTix({
        ticket_id: +values.ticket?.value,
        num_tickets: values.quantity,
        event_id: +id,
      });

      if (!response || response.errors) {
        errorMessage({}, 'Error generating Vet Tix');
        return;
      } else {
        successMessage(
          'Successfully requested Vet Tix. A CSV will be emailed to you once all the tickets have been created.'
        );
      }
    } catch (error) {
      console.error('Error generating Vet Tix:', error);
      errorMessage({}, 'Error generating Vet Tix');
    } finally {
      setIsSubmitting(false);
    }

    setIsVisible(false);
  };

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      maxWidth={600}
      height={500}
      hideModal={() => setIsVisible(false)}
    >
      <Content>
        <Title>Generate Vet Tix CSV Export</Title>

        <Formik
          initialValues={{ ticket: '', quantity: undefined }}
          enableReinitialize={true}
          validateOnChange={false}
          validateOnBlur={false}
          validate={(values) => {
            const errors = {};

            if (!values.ticket) {
              errors.ticket = 'Required';
              errorMessage({}, 'Ticket is required');
            }
            if (!values.quantity) {
              errors.quantity = 'Required';
              errorMessage({}, 'Quantity is required');
            } else if (values.quantity < 1) {
              errors.quantity = 'Must be greater than 0';
              errorMessage({}, 'Quantity must be greater than 0');
            }

            return errors;
          }}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, setFieldValue, errors, touched }) => (
            <Form
              style={{
                minHeight: 500,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                paddingInline: 20,
              }}
            >
              <div>
                <FieldWrapper>
                  {loading ? (
                    <Loading />
                  ) : (
                    <AutoSuggest
                      id="ticket"
                      label="Select the ticket you want to give away."
                      placeholder="Select ..."
                      options={tickets}
                      onChange={(val) => setFieldValue('ticket', val)}
                      value={values.ticket}
                      error={touched.ticket && errors.ticket}
                      name="ticket"
                    />
                  )}
                </FieldWrapper>

                <FieldWrapper>
                  <Input
                    name="quantity"
                    type="number"
                    value={values.quantity}
                    onChange={handleChange}
                    error={touched.quantity && errors.quantity}
                    label={'How many tickets do you want to give away?'}
                  />
                </FieldWrapper>
              </div>

              {isSubmitting ? (
                <Loading />
              ) : (
                <SubmitButton type="submit">Submit</SubmitButton>
              )}
            </Form>
          )}
        </Formik>
      </Content>
    </Modal>
  );
};
